import React from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaCheck, FaEdit } from 'react-icons/fa';

const Step3SubmittedDataModal = ({
  isOpen,
  onClose,
  customTable,
  onMarkCompleted,
  onEditTable,
  step3Status
}) => {
  if (!isOpen || !customTable) return null;

  const { columns, rows, customRows, userSubmittedData, userSubmittedAt } = customTable;

  const tableRows = customRows || rows?.map(r => ({
    adminValue: r[0],
    isMandatory: columns?.[0]?.isMandatory ?? true
  })) || [];

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 dark:border-dark-700">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Step 3 — Client Submitted Data
              </h2>
              {userSubmittedAt && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Submitted on: {new Date(userSubmittedAt).toLocaleString()}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors shrink-0"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {userSubmittedData && userSubmittedData.length > 0 ? (
            <div className="border border-gray-200 dark:border-dark-600 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-dark-700">
                    <tr>
                      <th className="px-3 py-2 border-b border-gray-200 dark:border-dark-600 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 text-left w-[45%]">
                        Information Required
                      </th>
                      <th className="px-3 py-2 border-b border-gray-200 dark:border-dark-600 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 text-left w-[55%]">
                        Client Remarks
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {userSubmittedData.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className="odd:bg-white even:bg-gray-50 dark:odd:bg-dark-800 dark:even:bg-dark-700/70 border-b border-gray-100 dark:border-dark-700 last:border-b-0"
                      >
                        <td className="px-3 py-2.5 align-top">
                          <div className="flex gap-2">
                            <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 tabular-nums shrink-0">
                              {(rowIndex + 1).toString().padStart(2, '0')}
                            </span>
                            <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
                              {tableRows[rowIndex]?.adminValue || row[0] || '—'}
                            </p>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="px-3 py-2 rounded-xl bg-gray-50 dark:bg-dark-900/50 border border-gray-100 dark:border-dark-700 text-sm text-gray-800 dark:text-gray-200 min-h-[2.5rem]">
                            {row[1] || <span className="text-gray-400 dark:text-gray-500 italic text-xs">No remarks provided</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="p-6 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 text-center">
              <h3 className="text-sm font-bold text-amber-900 dark:text-amber-300 mb-1">Pending Submission</h3>
              <p className="text-xs text-amber-700 dark:text-amber-400">
                The client has not yet submitted their remarks for this Step 3 form.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 flex justify-between gap-2 border-t border-gray-100 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-900/40">
          <div>
            {onEditTable && (step3Status === 'client-pending' || step3Status === 'submitted') && (
              <button
                type="button"
                onClick={onEditTable}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-800 rounded-xl hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <FaEdit size={12} />
                Edit Table
              </button>
            )}
          </div>
          <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-dark-600 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
          >
            Close
          </button>
          {userSubmittedData && userSubmittedData.length > 0 && (
            <button
              type="button"
              onClick={onMarkCompleted}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
            >
              <FaCheck size={12} />
              Mark Step 3 as Completed
            </button>
          )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Step3SubmittedDataModal;
